import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
// How far a finger has to travel across for a swipe - less is a tap, or a
// photo nudged by accident.
const SWIPE_DISTANCE = 50;

/**
 * Click-to-zoom for images inside a post's own body (see
 * showPostImagesInLightbox.js for what opens this), with prev/next
 * navigation through every image in the discussion when there's more
 * than one - deliberately simpler than that navigation otherwise: no
 * source footer, no fullscreen-API dance like champis-species'/
 * champis-paintings' own gallery modals, since a post photo's own
 * discussion is already the page behind the modal (a caption, when the
 * migrated phpBB attachment had one - see ImageCaptionFix/
 * ImageCaptionRenderer, migration/ - still shows, in the same floating
 * footer as the photo count, mirroring SpeciesGalleryModal's own
 * -footer). Escape/backdrop-click/the close button all already dismiss
 * it for free - Modal's own isDismissibleViaEscKey (and the other two)
 * default to true, nothing extra to wire up here for that.
 *
 * Clicking the image itself toggles between fit-to-modal and its natural
 * (full-resolution) size - needed to check fine details on a specimen
 * photo. No custom drag/pinch code: once zoomed, the now-oversized image
 * simply overflows PostImageLightbox-body, whose own overflow: auto lets
 * the native scrollbar/trackpad/touch-drag pan it in both directions.
 * Navigating to a different image resets zoomed back to false - the next
 * photo is a different size and starting it pre-zoomed would be jarring.
 * On a phone, a swipe goes to the next or previous photo too (see
 * onTouchEnd()) - but not once zoomed, where a finger pans instead.
 *
 * The zoomed modifier class is applied to the -body div inside content(),
 * never to className() (the modal's own root element): ModalManager
 * toggles a Bootstrap-style "in" class on that root element imperatively
 * (classList.add, outside Mithril's vdom), and a className() that changes
 * across redraws makes Mithril overwrite the whole class attribute on the
 * next redraw, silently wiping that "in" class back out - the modal was
 * still "open" in JS state but invisible (opacity: 0 from the base .fade
 * rule). content() has no such conflict: Mithril fully owns that subtree.
 */
export default class PostImageLightbox extends Modal {
  index = 0;
  zoomed = false;
  // Where a one-finger touch began - see onTouchEnd().
  touchStart = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.index = this.attrs.index || 0;
    this.zoomed = false;
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.onkeydown = this.onkeydown.bind(this);
    document.addEventListener('keydown', this.onkeydown);
  }
  onremove(vnode) {
    super.onremove(vnode);
    document.removeEventListener('keydown', this.onkeydown);
  }
  onkeydown(e) {
    if (e.key === 'ArrowLeft') this.previous();
    if (e.key === 'ArrowRight') this.next();
  }
  className() {
    return 'Modal--large PostImageLightbox';
  }
  title() {
    return '';
  }
  content() {
    const images = this.attrs.images;
    const image = images[this.index];
    const hasMultiple = images.length > 1;
    return m("div", {
      className: 'Modal-body PostImageLightbox-body' + (this.zoomed ? ' PostImageLightbox-body--zoomed' : ''),
      ontouchstart: e => this.onTouchStart(e),
      ontouchend: e => this.onTouchEnd(e)
    }, hasMultiple && m(Button, {
      icon: "fas fa-chevron-left",
      className: "Button Button--icon PostImageLightbox-nav PostImageLightbox-nav--prev",
      onclick: () => this.previous(),
      "aria-label": app.translator.trans('champis-forum.forum.lightbox.previous')
    }), m("img", {
      src: image.url,
      alt: image.alt || '',
      onclick: () => {
        this.zoomed = !this.zoomed;
      }
    }), hasMultiple && m(Button, {
      icon: "fas fa-chevron-right",
      className: "Button Button--icon PostImageLightbox-nav PostImageLightbox-nav--next",
      onclick: () => this.next(),
      "aria-label": app.translator.trans('champis-forum.forum.lightbox.next')
    }), (hasMultiple || image.caption) && m("div", {
      className: "PostImageLightbox-footer"
    }, hasMultiple && m("span", {
      className: "PostImageLightbox-count"
    }, this.index + 1, " / ", images.length), image.caption && m("span", {
      className: "PostImageLightbox-caption"
    }, image.caption)));
  }
  onTouchStart(e) {
    // A second finger (a pinch) is no swipe.
    this.touchStart = e.touches.length === 1 ? {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    } : null;
  }

  /**
   * A swipe: right to left for the next photo, left to right for the
   * previous one - at least SWIPE_DISTANCE across, and mostly across rather
   * than up or down, so a tap (the zoom) or a scroll isn't one.
   */
  onTouchEnd(e) {
    const start = this.touchStart;
    this.touchStart = null;
    if (!start || this.zoomed || this.attrs.images.length < 2) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) < 2 * Math.abs(dy)) return;
    if (dx < 0) {
      this.next();
    } else {
      this.previous();
    }
  }
  previous() {
    const count = this.attrs.images.length;
    this.index = (this.index - 1 + count) % count;
    this.zoomed = false;
    m.redraw();
  }
  next() {
    const count = this.attrs.images.length;
    this.index = (this.index + 1) % count;
    this.zoomed = false;
    m.redraw();
  }
}
flarum.reg.add('champis-forum', 'forum/components/PostImageLightbox', PostImageLightbox);